import { listBindings as _lb, addBinding as _ab, removeBinding as _rb } from './entraApi.js';
// Policies + Data Shield data layer, plus the client-side detection engine.
//
// The detection engine is intentionally pure (no Supabase, no React) so it can
// run anywhere: the test harness, the live prompt simulator, and - eventually -
// a real edge gateway sharing the same regex source from the DB.

import { supabase } from '@govern360/shared';
// G360_K840 - one definition of "which statuses does this rule cover", shared
// with shieldModel and the pages. shieldModel.js imports nothing, so there is
// no cycle here.
import { scopeStatusesOf } from './shieldModel.js';

/* ============================== Detectors ============================== */
/* G360_TENANT_SCOPE (K846) - filter in the QUERY, not only on arrival.
   Both callers already discarded other tenants' rows after the fetch, so the
   screen was correct. But a staff user - whose RLS policy ends
   OR is_super_admin(), returning every tenant's rows - was still pulling
   EVERY customer's detectors into the browser, names and regex patterns
   included, while looking at one of them. Discarding data after it has
   crossed the boundary is not scoping it.
   It was also fragile: correctness depended on every consumer repeating the
   same filter line. Two did. A third would not have.
   The arrival-side filters are KEPT as belt and braces, and they still handle
   the no-organisation-selected case.
   tenant_id IS NULL rows are the shared defaults and must still come back,
   which is why this is an .or() and not an .eq(). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function listDetectors(tenantId) {
  let q = supabase
    .from('detectors').select('*').order('category').order('name');
  if (tenantId) {
    /* .or() takes a filter STRING, so the value is interpolated. It comes from
       our own auth state rather than user input, but a governance product does
       not interpolate an unvalidated value into a query filter. A malformed id
       returns an error rather than falling back to every tenant's rows, which
       would be the exact defect this function exists to fix. */
    if (!UUID_RE.test(String(tenantId))) {
      return { data: [], error: { message: 'Detectors were not loaded: the organisation id is not a valid identifier. Listing every organisation\'s detectors instead would not be an acceptable fallback.' } };
    }
    q = q.or(`tenant_id.is.null,tenant_id.eq.${tenantId}`);
  }
  const { data, error } = await q;
  return { data: data || [], error };
}
export async function listDetectorCatalog() {
  const { data, error } = await supabase
    .from('detector_catalog').select('*').order('category').order('name');
  return { data: data || [], error };
}
export async function enableDefaultDetectors() {
  const { data, error } = await supabase.rpc('enable_default_detectors');
  return { count: data, error };
}
export async function createDetector(tenantId, d) {
  const { data, error } = await supabase
    .from('detectors').insert({ ...d, tenant_id: tenantId, is_custom: true }).select().single();
  return { data, error };
}
export async function updateDetector(id, updates) {
  const { data, error } = await supabase
    .from('detectors').update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id).select().single();
  return { data, error };
}
export async function setDetectorEnabled(id, enabled) {
  return updateDetector(id, { enabled });
}
export async function deleteDetector(id) {
  const { error } = await supabase.from('detectors').delete().eq('id', id);
  return { error };
}

/* ============================== Policies ============================== */
// G360_TENANT_SCOPE - filter explicitly rather than leaving it to RLS.
// A staff user's policy ends OR is_super_admin(), which returns every
// tenant's rows, so without this the page ignores the organization
// switcher and blends customers together.
export async function listPolicies(tenantId) {
  let q = supabase
    .from('policies').select('*').order('priority').order('created_at');
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q;
  return { data: data || [], error };
}
export async function createPolicy(tenantId, p) {
  const { data, error } = await supabase
    .from('policies').insert({ ...p, tenant_id: tenantId }).select().single();
  return { data, error };
}
export async function updatePolicy(id, updates) {
  const { data, error } = await supabase
    .from('policies').update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id).select().single();
  return { data, error };
}
export async function setPolicyEnabled(id, enabled) {
  return updatePolicy(id, { enabled });
}
export async function deletePolicy(id) {
  const { error } = await supabase.from('policies').delete().eq('id', id);
  return { error };
}
export async function seedDemoPolicies() {
  const { data, error } = await supabase.rpc('seed_demo_policies');
  return { count: data, error };
}

/* ============================ Enforcement ============================ */
export async function listEnforcement(limit = 25) {
  const { data, error } = await supabase
    .from('enforcement_events').select('*')
    .order('created_at', { ascending: false }).limit(limit);
  return { data: data || [], error };
}
export async function recordEnforcement({ tool, action, summary, policyName, detectorKeys }) {
  const { error } = await supabase.rpc('record_enforcement', {
    p_tool: tool, p_action: action, p_summary: summary,
    p_policy_name: policyName || null, p_detector_keys: detectorKeys || null,
  });
  return { error };
}

/* ====================== Client-side detection engine ====================== */

// Compile a detector row into a usable RegExp, guarding against bad patterns.
function compile(det) {
  try {
    return new RegExp(det.pattern, det.flags || 'g');
  } catch {
    return null; // invalid regex - skip rather than crash the harness
  }
}

/**
 * runDetectors(text, detectors) - find all matches.
 * Returns: [{ detector, key, name, category, action, severity, matches: [{value, index}] }]
 * Only enabled detectors are considered.
 */
export function runDetectors(text, detectors) {
  const findings = [];
  if (!text) return findings;

  for (const det of detectors) {
    if (det.enabled === false) continue;
    const re = compile(det);
    if (!re) continue;

    const matches = [];
    if (re.global) {
      let m;
      let guard = 0;
      while ((m = re.exec(text)) !== null && guard < 500) {
        matches.push({ value: m[0], index: m.index });
        if (m.index === re.lastIndex) re.lastIndex++; // avoid zero-width loop
        guard++;
      }
    } else {
      const m = re.exec(text);
      if (m) matches.push({ value: m[0], index: m.index });
    }

    if (matches.length) {
      findings.push({
        key: det.key, name: det.name, category: det.category,
        action: det.action, severity: det.severity, matches,
      });
    }
  }
  return findings;
}

/**
 * evaluatePolicies(findings, policies, ctx) - given detector findings and the
 * tenant's policies, decide the final verdict.
 *
 * ctx = { toolStatus } so status-scoped policies ('shadow' only) apply correctly.
 *
 * Returns: { verdict, firedPolicy, reason } where verdict is the most severe
 * action among matching policies. Severity order: block > redact > warn > allow.
 */
const ACTION_RANK = { allow: 0, warn: 1, redact: 2, block: 3 };

export function evaluatePolicies(findings, policies, ctx = {}) {
  const foundKeys = new Set(findings.map((f) => f.key));
  let best = { verdict: 'allow', firedPolicy: null, reason: 'No policy matched - allowed.' };

  // policies are pre-sorted by priority; evaluate all, keep most severe.
  for (const p of policies) {
    if (p.enabled === false) continue;

    // Scope check.
    // G360_K840 - a rule may name several statuses. This evaluator is what the
    // console preview and simulator use, so it has to match the guard exactly or
    // the preview lies about what will happen.
    const scopeStatuses = scopeStatusesOf(p);
    const statusScoped = p.scope_type === 'status' && scopeStatuses.length > 0;
    if (statusScoped && ctx.toolStatus && scopeStatuses.indexOf(ctx.toolStatus) === -1) continue;

    // Condition check.
    //   - detector_keys non-empty - fires when any listed detector matches
    //   - empty + status scope    - the scope itself is the trigger (content irrelevant)
    //   - empty + all scope       - fires only if something was detected
    const keys = p.detector_keys || [];
    let matched;
    if (keys.length > 0) {
      matched = keys.some((k) => foundKeys.has(k));
    } else if (statusScoped) {
      matched = true;
    } else {
      matched = findings.length > 0;
    }
    if (!matched) continue;

    if (ACTION_RANK[p.action] > ACTION_RANK[best.verdict]) {
      best = {
        verdict: p.action,
        firedPolicy: p,
        reason: `Policy "${p.name}" -> ${p.action.toUpperCase()}`,
      };
    }
  }
  return best;
}

/**
 * redactText(text, findings) - produce a redacted copy where matched spans
 * are replaced with masking characters
 * sanitized prompt would look like.
 */
export function redactText(text, findings) {
  // Collect all match spans, redact those whose detector action is redact/block.
  const spans = [];
  for (const f of findings) {
    if (f.action === 'redact' || f.action === 'block') {
      for (const m of f.matches) spans.push([m.index, m.index + m.value.length]);
    }
  }
  if (!spans.length) return text;
  spans.sort((a, b) => a[0] - b[0]);
  // Merge overlaps.
  const merged = [];
  for (const s of spans) {
    if (merged.length && s[0] <= merged[merged.length - 1][1]) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], s[1]);
    } else merged.push([...s]);
  }
  let out = '';
  let cursor = 0;
  for (const [start, end] of merged) {
    out += text.slice(cursor, start) + '*'.repeat(Math.min(end - start, 12));
    cursor = end;
  }
  out += text.slice(cursor);
  return out;
}

/* ============================== UI constants ============================== */
export const ACTIONS = {
  allow:  { label: 'Allow',  pill: 'p-succ' },
  warn:   { label: 'Warn',   pill: 'p-info' },
  redact: { label: 'Redact', pill: 'p-warn' },
  block:  { label: 'Block',  pill: 'p-dang' },
};
export const ACTION_OPTIONS = [
  { value: 'allow', label: 'Allow' },
  { value: 'warn', label: 'Warn' },
  { value: 'redact', label: 'Redact' },
  { value: 'block', label: 'Block' },
];
export const DETECTOR_CATEGORIES = ['Identity', 'Payment', 'Secret', 'Contact', 'PHI', 'IP', 'Confidential'];

const _AI_BINDINGS = 'ai_policy_group_bindings';
export const listPolicyGroups  = (policyId)       => _lb(_AI_BINDINGS, policyId);
export const addPolicyGroup    = (tid, pid, k, g) => _ab(_AI_BINDINGS, tid, pid, k, g);
export const removePolicyGroup = (bindingId)      => _rb(_AI_BINDINGS, bindingId);

// All Entra group bindings across the tenant's policies (RLS-scoped).
// Used by the Conditional Access recommendation mapper (Chunk 3a).
export async function listAllPolicyBindings(tenantId) {
  // G360_K461 - optional tenant, as listPolicies: RLS returns every
  // organisation the user belongs to (staff: all).
  let q = supabase
    .from('ai_policy_group_bindings')
    .select('policy_id, kind, entra_group_id, display_name');
  if (tenantId) q = q.eq('tenant_id', tenantId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/* G360_K461 - one organisation's policies and their group bindings, for
   the enforcement plane exports. Every export read listPolicies() and
   listAllPolicyBindings() with no tenant, so a member of two organisations
   - and staff - exported SASE rules, gateway config, Purview and Intune
   specs and CA recommendations built from BOTH organisations' policies.
   And listPolicies' error was never looked at, so a failed read exported
   an empty rule set as if there were no policies. This reads the
   organisation named, and throws when the policies could not be read. */
export async function policyScope(tenantId) {
  const [polRes, binds] = await Promise.all([listPolicies(tenantId), listAllPolicyBindings(tenantId)]);
  if (polRes && polRes.error) throw new Error(polRes.error.message || 'the policies could not be read');
  const policies = (polRes && polRes.data) || [];
  const ids = new Set(policies.map((p) => p.id));
  const byPolicy = {};
  for (const b of binds || []) {
    if (!ids.has(b.policy_id)) continue;
    (byPolicy[b.policy_id] = byPolicy[b.policy_id] || []).push(b);
  }
  return { policies, byPolicy };
}
